import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import type {
  LeadAssignmentPolicy,
  UpdateLeadAssignmentPolicyInput,
} from "@school-sales-ai/shared";
import { api } from "@/lib/api";
import { useScopedClientId } from "@/hooks/useScopedClientId";

const BASE = "/crm/lead-assignment";

/**
 * Everything a policy change can move: which leads each member can see, and
 * every list, count and badge built from that. Invalidated wholesale — a
 * policy flip is rare, and a member staring at a list the server would no
 * longer return is the bug to avoid.
 */
function invalidateLeadReads(queryClient: QueryClient): void {
  for (const key of [
    "lead-assignment",
    "leads",
    "lead",
    "pipeline",
    "pipeline-stage",
    "inbox",
    "inbox-unread",
    "product-counts",
    "cycle-counts",
    "label-counts",
    "broadcast-preview",
    "report-overview",
  ]) {
    void queryClient.invalidateQueries({ queryKey: [key] });
  }
}

/**
 * The client's lead assignment policy and team. Readable by every role: it
 * decides whether the «Asignado a» filter, column and control render at all,
 * and resolves an `assignedProfileId` to a name.
 */
export function useLeadAssignmentPolicy() {
  const clientId = useScopedClientId();
  return useQuery({
    queryKey: ["lead-assignment", clientId],
    queryFn: () => api.get<LeadAssignmentPolicy>(BASE),
    enabled: !!clientId,
    staleTime: 60_000,
  });
}

/** True only once the policy has loaded AND is on — never on a guess. */
export function useLeadAssignmentEnabled(): boolean {
  const { data } = useLeadAssignmentPolicy();
  return data?.enabled === true;
}

export function useSetLeadAssignmentPolicy() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (patch: UpdateLeadAssignmentPolicyInput) =>
      api.put<LeadAssignmentPolicy>(BASE, patch),
    onSuccess: () => {
      invalidateLeadReads(queryClient);
      toast.success("Política de asignación guardada.");
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

export function useSetReceivesLeads() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: { profileId: string; receivesLeads: boolean }) =>
      api.put<LeadAssignmentPolicy>(`${BASE}/members/${vars.profileId}`, {
        receivesLeads: vars.receivesLeads,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["lead-assignment"] });
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

/** Take, release, transfer or place one lead. `profileId: null` unassigns. */
export function useChangeAssignee(leadId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (profileId: string | null) =>
      api.put<{ id: string; assignedProfileId: string | null }>(
        `/crm/leads/${leadId}/assignee`,
        { profileId },
      ),
    onSuccess: () => {
      // Releasing or transferring can take the lead out of this member's own
      // scope, so every list is refetched, not just this lead.
      invalidateLeadReads(queryClient);
      toast.success("Responsable actualizado.");
    },
    onError: (err: Error) => {
      toast.error(err.message);
      void queryClient.invalidateQueries({ queryKey: ["lead", leadId] });
    },
  });
}
